<template>
  <div class="candidate-status-editor-page" v-rect-loading="loading">
    <div class="candidate-status-editor-header">
      <div class="candidate-status-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedForm
        ref="form"
        class="candidate-status-edit-form"
        label-width="220px"
        :model="formData"
        :formLines="formLines"
      >
      </WrappedForm>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { mapActions } from 'vuex'
import { addI18nProperty, selectLabelI18n } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedForm
  },
  props: {
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.candidateStatusManagement',
      candidateStatusList: [],
      formData: {}
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    causeId () {
      return this.$route.params.causeId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.label.editCandidateStatus' : '.label.addCandidateStatus'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    form () {
      return this.$refs.form
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        status: {
          elType: 'el-select',
          option: {
            data: this.candidateStatusList,
            labelKey: 'name',
            valueKey: 'key'
          },
          label: this.$t(`${this.i18nPrefix}.label.status`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.status`),
          rules: [{ required: true, message: requiredMessage, trigger: 'change' }]
        },
        cause_cn: {
          label: this.$t(`${this.i18nPrefix}.label.causeCN`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.causeCN`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        cause_en: {
          label: this.$t(`${this.i18nPrefix}.label.causeEN`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.causeEN`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        }
      }
      return lines
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('candidateStatus', [
      'getCandidateStatusChangeCauseByCauseId',
      'getCandidateStatusList',
      'updateCandidateStatusChangeCause',
      'saveCandidateStatusChangeCause'
    ]),
    async initData () {
      this.loading = true
      const promiseList = [this.loadCandidateStatus()]
      if (this.isEditMode && this.causeId) {
        promiseList.push(this.loadCandidateStatusChangeCause())
      }
      try {
        await Promise.all(promiseList)
      } finally {
        this.loading = false
      }
    },
    async loadCandidateStatusChangeCause () {
      const params = {
        causeId: this.causeId,
        params: {
        }
      }
      const resp = await this.getCandidateStatusChangeCauseByCauseId(params)
      if (resp) {
        this.formData = {
          status: resp.status.key,
          cause_cn: resp.cause_cn,
          cause_en: resp.cause_en
        }
      }
    },
    async loadCandidateStatus () {
      const resp = await this.getCandidateStatusList()
      if (resp) {
        _.forEach(resp, candidateStatus => {
          addI18nProperty(candidateStatus, 'name', 'name_cn', 'name_en')
        })
        this.candidateStatusList = resp
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        let data = this.formData
        let saveFunc = this.saveCandidateStatusChangeCause
        if (this.isEditMode) {
          data = {
            causeId: this.causeId,
            data: this.formData
          }
          saveFunc = this.updateCandidateStatusChangeCause
        }
        const resp = await saveFunc(data)
        if (resp && resp === 'success') {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.$router.push('/v-admin/candidate-status/management/list')
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-status-editor-page {
  .candidate-status-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .candidate-status-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
